import { Button } from '@/components/Button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/Card'
import { dateFormatters } from '@/utils/date-util'
import { formatMoney } from '@/utils/money-util'
import styles from './JobSummaryCard.module.css'
import { pluralize } from '@/utils/pluralization-util'
import { capitalize } from 'lodash-es'
import { type PrepAndReviewLinkType } from '@/features/jobs/job-type'

interface JobSummaryItemProps {
  label: string
  value: string
  buttonText?: string
  onPress?: () => void
  testId?: string
  buttonTestId?: string
}

function JobSummaryItem({
  label,
  value,
  buttonText,
  onPress,
  testId,
  buttonTestId,
}: JobSummaryItemProps) {
  return (
    <div className={styles.jobSummaryItem}>
      <div className={styles.jobSummaryItemLabel}>{label}</div>
      <div className={styles.jobSummaryItemValueContainer}>
        <div className={styles.jobSummaryItemValue} data-test={testId}>
          {value}
        </div>
        {buttonText && onPress && (
          <div className={styles.jobSummaryItemAction}>
            <Button
              label={buttonText}
              className={styles.jobSummaryItemButton}
              variant="text"
              onPress={onPress}
              testId={buttonTestId}
            />
          </div>
        )}
      </div>
    </div>
  )
}

export interface JobSummaryCardProps {
  shortId: string
  workspaceName: string
  showConsultationCallTime: boolean
  consultationCallTime?: string
  showLegend: boolean
  legendText: string
  serviceItemsCount: number
  onViewServiceItems: () => unknown
  bundleName?: string
  showProviderCut: boolean
  formattedProviderCutAmount: string
  dateSubmitted: Date | null
  dateDeadline: Date | null
  showPrepAndReview: boolean
  prepAndReviewJobText: PrepAndReviewLinkType
  prepAndReviewJobLinkType?: PrepAndReviewLinkType | null
  onViewPrepAndReviewJob: () => unknown
  showJobTotal: boolean
  total: number
}

export function JobSummaryCard({
  shortId,
  workspaceName,
  showConsultationCallTime,
  consultationCallTime,
  showLegend,
  legendText,
  serviceItemsCount,
  onViewServiceItems,
  bundleName,
  showProviderCut,
  formattedProviderCutAmount,
  dateSubmitted,
  dateDeadline,
  showPrepAndReview,
  prepAndReviewJobText,
  prepAndReviewJobLinkType,
  onViewPrepAndReviewJob,
  showJobTotal,
  total,
}: JobSummaryCardProps) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Job summary</CardTitle>
      </CardHeader>
      <CardContent scroll>
        <JobSummaryItem label="ID" value={shortId} />
        <JobSummaryItem label="Workspace" value={workspaceName} />

        {showConsultationCallTime && consultationCallTime && (
          <JobSummaryItem
            label="Consultation call time"
            value={consultationCallTime}
            testId="job-consultation-time"
          />
        )}
        {showLegend && (
          <JobSummaryItem
            label="Legend"
            value={legendText}
            testId="job-legend-name"
          />
        )}
        {serviceItemsCount > 0 && (
          <JobSummaryItem
            label="Service items"
            value={`${serviceItemsCount} ${pluralize(
              'item',
              serviceItemsCount,
            )}`}
            buttonText="View list"
            onPress={onViewServiceItems}
          />
        )}
        {bundleName !== undefined && (
          <JobSummaryItem label="Bundle" value={bundleName} />
        )}
        {showProviderCut && (
          <JobSummaryItem
            label="Pro cut"
            value={formattedProviderCutAmount}
            testId="job-pro-cut-amount"
          />
        )}
        {dateSubmitted && (
          <JobSummaryItem
            label="Date submitted"
            value={dateFormatters.dateTimeDefault.format(dateSubmitted)}
          />
        )}
        {dateDeadline && (
          <JobSummaryItem
            label="Deadline"
            value={dateFormatters.dateTimeDefault.format(dateDeadline)}
          />
        )}

        {showPrepAndReview && (
          <JobSummaryItem
            label="Prep and review"
            value={`${capitalize(prepAndReviewJobText)} job`}
            buttonText={`View ${prepAndReviewJobLinkType?.toLowerCase()} job`}
            onPress={onViewPrepAndReviewJob}
            testId={`prep-and-review-job-type-${prepAndReviewJobText.toUpperCase()}`}
            buttonTestId={`prep-and-review-job-type-${prepAndReviewJobLinkType?.toUpperCase()}-btn`}
          />
        )}

        {showJobTotal && (
          <JobSummaryItem
            label="Total"
            value={formatMoney(total)}
            testId="job-total"
          />
        )}
      </CardContent>
    </Card>
  )
}
